import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    TextField,
    Popover,
    IconButton,
    Typography,
    Grid,
    Button,
    InputAdornment,
    Autocomplete,
    Stack,
} from '@mui/material';
import type { TextFieldProps as MUITextFieldProps } from '@mui/material/TextField';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import { alpha, useTheme } from '@mui/material/styles';
import {
    DATE_FORMAT_HINT,
    MONTH_YEAR_FORMAT_HINT,
    normalizeToDdMmYyyy,
    parseMMYYYY,
    formatMMYYYY,
    formatMonthYearInput,
    formatDateInput,
} from '@/utils/dataUtils';

export interface DatePickerProps {
    label?: string;
    value: string | null; // DD/MM/YYYY
    onChange: (value: string | null) => void;
    minDate?: string; // DD/MM/YYYY
    maxDate?: string; // DD/MM/YYYY
    fromDate?: string; // DD/MM/YYYY - only allow dates on or after this date (inclusive)
    toDate?: string; // DD/MM/YYYY - only allow dates on or before this date (inclusive)
    placeholder?: string;
    display?: boolean; // display mode: render content only (no input chrome)
    fullWidth?: boolean;
    required?: boolean;
    error?: boolean;
    helperText?: React.ReactNode;
    name?: string;
    size?: 'small' | 'medium';
    variant?: 'outlined' | 'filled' | 'standard';
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
    inputProps?: React.ComponentProps<typeof TextField>['inputProps'];
    InputLabelProps?: MUITextFieldProps['InputLabelProps'];
    compact?: boolean; // mimic InputField compact style
    align?: 'left' | 'center' | 'right'; // text alignment (default: right)
    /** Hover label tip content (same pattern as InputField.popoverContent) */
    popoverContent?: React.ReactNode;
    /**
     * day = DD/MM/YYYY (default). month = MM/YYYY input and month grid.
     * minDate/maxDate stay DD/MM/YYYY and are compared by calendar month.
     */
    precision?: 'day' | 'month';
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function parseYMD(value?: string | null): Date | null {
    if (!value) return null;
    // Expect DD/MM/YYYY
    const m = /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/.exec(value);
    if (!m) return null;
    const d = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const y = Number(m[3]);
    const dt = new Date(y, mo, d);
    // validate roundtrip
    if (
        dt.getFullYear() === y &&
        dt.getMonth() === mo &&
        dt.getDate() === d
    ) {
        return dt;
    }
    return null;
}

function toYMD(date: Date): string {
    // Return as DD/MM/YYYY
    const d = `${date.getDate()}`.padStart(2, '0');
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const y = `${date.getFullYear()}`;
    return `${d}/${m}/${y}`;
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
function isBefore(a: Date, b: Date) {
    return startOfDay(a).getTime() < startOfDay(b).getTime();
}
function isAfter(a: Date, b: Date) {
    return a.getTime() > b.getTime();
}

export function DatePicker({
    label,
    value,
    onChange,
    minDate,
    maxDate,
    fromDate,
    toDate,
    placeholder,
    display,
    fullWidth = true,
    required,
    error,
    helperText,
    name,
    size = 'small',
    variant = 'outlined',
    onBlur,
    inputProps,
    InputLabelProps,
    compact = true,
    align = 'right',
    popoverContent,
    precision = 'day',
}: DatePickerProps) {
    const anchorRef = useRef<HTMLInputElement | null>(null);
    const [open, setOpen] = useState(false);
    const theme = useTheme();
    // Label tip popover (must stay above display early return)
    const tipLabelRef = useRef<HTMLElement | null>(null);
    const [tipAnchorEl, setTipAnchorEl] = useState<HTMLElement | null>(null);
    const leaveTipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasTip = !!popoverContent;
    const tipOpen = Boolean(tipAnchorEl) && hasTip;
    const enterTip = () => {
        if (!hasTip) return;
        if (leaveTipTimerRef.current) {
            clearTimeout(leaveTipTimerRef.current);
            leaveTipTimerRef.current = null;
        }
        setTipAnchorEl(tipLabelRef.current);
    };
    const leaveTip = () => {
        if (leaveTipTimerRef.current) clearTimeout(leaveTipTimerRef.current);
        leaveTipTimerRef.current = setTimeout(() => {
            setTipAnchorEl(null);
            leaveTipTimerRef.current = null;
        }, 150);
    };
    const tipPaperProps = {
        onMouseEnter: enterTip,
        onMouseLeave: leaveTip,
        sx: {p: 1, maxWidth: 'none', pointerEvents: 'auto' as const},
    };

    const isMonth = precision === 'month';
    const selected = useMemo(
        () => (isMonth ? (parseMMYYYY(value) || parseYMD(value)) : parseYMD(value)),
        [value, isMonth],
    );
    
    // Parse date constraints
    const min = useMemo(() => {
        if (fromDate) return parseYMD(fromDate) || undefined;
        return parseYMD(minDate) || undefined;
    }, [minDate, fromDate]);
    
    const max = useMemo(() => {
        if (toDate) return parseYMD(toDate) || undefined;
        return parseYMD(maxDate) || undefined;
    }, [maxDate, toDate]);

    // text input state for Autocomplete (freeSolo)
    const [inputValue, setInputValue] = useState<string>(() => {
        if (isMonth) return (parseMMYYYY(value) || parseYMD(value)) ? formatMMYYYY((parseMMYYYY(value) || parseYMD(value)) as Date) : (value || '');
        return normalizeToDdMmYyyy(value) || value || '';
    });
    const [formatError, setFormatError] = useState<string | undefined>();
    useEffect(() => {
        if (isMonth) {
            const parsed = parseMMYYYY(value) || parseYMD(value);
            setInputValue(parsed ? formatMMYYYY(parsed) : (value || ''));
            if (!value?.trim() || parsed) setFormatError(undefined);
            return;
        }
        const normalized = normalizeToDdMmYyyy(value);
        setInputValue(normalized || value || '');
        if (!value?.trim() || normalized) {
            setFormatError(undefined);
        }
    }, [value, isMonth]);

    // Visible month state; defaults to selected date's month, else today
    const [viewYear, setViewYear] = useState<number>(() => selected?.getFullYear() ?? new Date().getFullYear());
    const [viewMonth, setViewMonth] = useState<number>(() => selected?.getMonth() ?? new Date().getMonth()); // 0..11

    const handleOpen = () => {
        if (display) return;
        // sync visible month with selected when opening
        if (selected) {
            setViewYear(selected.getFullYear());
            setViewMonth(selected.getMonth());
        }
        setOpen(true);
    };
    const handleClose = () => setOpen(false);

    const startOfMonth = useMemo(() => new Date(viewYear, viewMonth, 1), [viewYear, viewMonth]);
    const endOfMonth = useMemo(() => new Date(viewYear, viewMonth + 1, 0), [viewYear, viewMonth]);
    const startWeekday = startOfMonth.getDay(); // 0..6 (Sun..Sat)
    const daysInMonth = endOfMonth.getDate();

    const weeks: Array<Array<Date | null>> = [];
    let current: Array<Date | null> = [];
    // Fill leading blanks
    for (let i = 0; i < startWeekday; i++) current.push(null);
    // Fill days
    for (let day = 1; day <= daysInMonth; day++) {
        current.push(new Date(viewYear, viewMonth, day));
        if (current.length === 7) {
            weeks.push(current);
            current = [];
        }
    }
    // Fill trailing blanks
    if (current.length) {
        while (current.length < 7) current.push(null);
        weeks.push(current);
    }

    const canGoPrev = useMemo(() => {
        if (!min) return true;
        const prevEnd = new Date(viewYear, viewMonth, 0); // last day of previous month
        return !isBefore(prevEnd, min);
    }, [min, viewYear, viewMonth]);

    const canGoNext = useMemo(() => {
        if (!max) return true;
        const nextStart = new Date(viewYear, viewMonth + 1, 1); // first day of next month
        return !isAfter(nextStart, max);
    }, [max, viewYear, viewMonth]);

    const canGoPrevYear = useMemo(() => {
        if (!min) return true;
        const prevYearEnd = new Date(viewYear - 1, viewMonth + 1, 0);
        return !isBefore(prevYearEnd, min);
    }, [min, viewYear, viewMonth]);

    const canGoNextYear = useMemo(() => {
        if (!max) return true;
        const nextYearStart = new Date(viewYear + 1, viewMonth, 1);
        return !isAfter(nextYearStart, max);
    }, [max, viewYear, viewMonth]);

    const handlePrevMonth = () => {
        if (!canGoPrev) return;
        const m = viewMonth - 1;
        if (m < 0) {
            setViewYear(viewYear - 1);
            setViewMonth(11);
        } else {
            setViewMonth(m);
        }
    };

    const handleNextMonth = () => {
        if (!canGoNext) return;
        const m = viewMonth + 1;
        if (m > 11) {
            setViewYear(viewYear + 1);
            setViewMonth(0);
        } else {
            setViewMonth(m);
        }
    };

    const handlePrevYear = () => {
        if (!canGoPrevYear) return;
        setViewYear(y => y - 1);
    };

    const handleNextYear = () => {
        if (!canGoNextYear) return;
        setViewYear(y => y + 1);
    };

    const isDisabledDate = (d: Date) => {
        // Month precision: compare YearMonth only (min/max may be any day in the bound month)
        if (isMonth) {
            const ym = d.getFullYear() * 12 + d.getMonth();
            if (min && ym < min.getFullYear() * 12 + min.getMonth()) return true;
            if (max && ym > max.getFullYear() * 12 + max.getMonth()) return true;
            return false;
        }
        if (min && isBefore(d, min)) return true;
        if (max && isAfter(d, max)) return true;
        return false;
    };

    const isSameDay = (a: Date, b: Date) =>
        a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

    const handleSelect = (d: Date) => {
        if (isDisabledDate(d)) return;
        const next = isMonth ? formatMMYYYY(d) : toYMD(d);
        setInputValue(next);
        onChange(next);
        setOpen(false);
    };

    const clearValue = (e?: React.MouseEvent) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        onChange(null);
    };

    const monthLabel = useMemo(() =>
        new Date(viewYear, viewMonth, 1).toLocaleString(undefined, { month: 'long', year: 'numeric' }),
    [viewYear, viewMonth]);

    // InputField-like styles
    const mergedSx = {
        position: 'relative',
        ...(compact ? { lineHeight: 1.2 } : {}),
        // Compact paddings for small size root
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall, & .MuiFilledInput-root.MuiInputBase-sizeSmall, & .MuiInputBase-root.MuiInputBase-sizeSmall': {
            paddingTop: '3px',
            paddingBottom: '3px',
            paddingLeft: '3px',
            backgroundColor: alpha(theme.palette.primary.main, 0.06),
        },
        // Input inner paddings and right space for icon
        '& .MuiInputBase-input': {
            fontSize: (theme.typography as any).body2?.fontSize || undefined,
            lineHeight: 1.2,
            paddingTop: 4,
            paddingBottom: 4,
            paddingRight: 32, // leave room for overlaid icon
        },
        // Position the end adornment icon 2px from the right across variants
        '& .MuiInputAdornment-positionEnd': {
            position: 'absolute',
            right: 2,
            top: '50%',
            transform: 'translateY(-50%)',
            margin: 0,
            height: 'auto',
            maxHeight: '100%',
            pointerEvents: 'auto',
        },
        // Reduce default padding on adorned end to avoid layout shift
        '& .MuiOutlinedInput-adornedEnd, & .MuiFilledInput-adornedEnd, & .MuiInputBase-adornedEnd': {
            paddingRight: 32,
        },
        // Hover on icon should follow variant border color
        '& .MuiInputAdornment-positionEnd .MuiIconButton-root:hover svg': {
            color: theme.palette.primary.main,
        },
    } as any;

    // Display mode: content only (aligned with MoneyField non-editable rendering)
    if (display) {
        const verticalAlign = align === 'center' ? 'center' : (align === 'right' ? 'flex-end' : 'flex-start');
        return (
            <>
                <Stack sx={{
                    padding: 0.5,
                    paddingLeft: label ? 1.5 : 0,
                    display: 'flex',
                    justifyContent: verticalAlign,
                    alignItems: align === 'center' ? 'center' : (align === 'right' ? 'flex-end' : 'flex-start'),
                }}>
                    {label && (
                        <Typography
                            align={align}
                            color="#00000099"
                            sx={{fontSize: 12, cursor: hasTip ? 'help' : undefined}}
                            ref={(node: HTMLElement | null) => { tipLabelRef.current = node; }}
                            onMouseEnter={hasTip ? enterTip : undefined}
                            onMouseLeave={hasTip ? leaveTip : undefined}
                        >
                            {label}
                        </Typography>
                    )}
                    <Typography
                        align={align}
                        component="span"
                        variant={compact ? 'body2' : 'body1'}
                        sx={{
                            fontSize: 12,
                            fontWeight: 500,
                            whiteSpace: 'nowrap',
                            textAlign: align,
                            ...(compact ? {lineHeight: 1.2} : {}),
                        }}
                    >
                        {value || '-'}
                    </Typography>
                </Stack>
                {hasTip && (
                    <Popover
                        open={tipOpen}
                        anchorEl={tipLabelRef.current}
                        onClose={leaveTip}
                        disableRestoreFocus
                        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                        transformOrigin={{vertical: 'center', horizontal: 'left'}}
                        sx={{pointerEvents: 'none'}}
                        PaperProps={tipPaperProps}
                    >
                        {popoverContent}
                    </Popover>
                )}
            </>
        );
    }

    return (
        <>
            <Autocomplete
                fullWidth={fullWidth}
                freeSolo
                options={[]}
                value={value || ''}
                inputValue={inputValue}
                onInputChange={(_, newInput) => {
                    let nextInput: string;
                    // ???嚗?餈撘??剁??芸頝唾? "/" ??蝚?
                    if (newInput.length < inputValue.length) {
                        let result = newInput;
                        if (result.endsWith('/')) {
                            // 撠暸 "/" ?芸蝘駁嚗? "01/01/2" ???? "01/01/" ??"01/01"嚗?
                            result = result.slice(0, -1);
                        } else if (inputValue[result.length] === '/') {
                            // 鋡怠??斤?摮泵??"/"嚗?憭宏?文?銝銝芸?蝚佗?憒?"01/01/" ??"01/0"嚗?
                            if (result.length > 0) {
                                result = result.slice(0, -1);
                            }
                        }
                        nextInput = result;
                    } else {
                        nextInput = isMonth ? formatMonthYearInput(newInput) : formatDateInput(newInput);
                    }
                    setInputValue(nextInput);
                    // 颲摰???交??嗥??喳?甇亙?嗥?隞塚??踹???blur 撠曹?摮紡?湔?桐腺憭?
                    if (!nextInput.trim()) {
                        if (value) onChange(null);
                    } else if (isMonth) {
                        const dt = parseMMYYYY(nextInput);
                        if (dt && !isDisabledDate(dt)) {
                            const next = formatMMYYYY(dt);
                            if (next !== value) onChange(next);
                        }
                    } else {
                        const dt = parseYMD(nextInput);
                        if (dt) {
                            const next = toYMD(dt);
                            if (next !== value) onChange(next);
                        }
                    }
                }}
                onChange={(_, newVal) => {
                    if (typeof newVal === 'string') {
                        const dt = isMonth ? parseMMYYYY(newVal) : parseYMD(newVal);
                        const next = dt ? (isMonth ? formatMMYYYY(dt) : toYMD(dt)) : null;
                        onChange(next ?? (newVal?.trim() ? newVal : null));
                    } else {
                        onChange(null);
                    }
                }}
                sx={{ width: '100%' }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        inputRef={anchorRef}
                        label={label}
                        placeholder={placeholder || (isMonth ? 'MM/YYYY' : 'dd/MM/yyyy')}
                        fullWidth={fullWidth}
                        required={required}
                        error={error || !!formatError}
                        helperText={formatError || helperText}
                        name={name}
                        size={size}
                        variant={variant}
                        onBlur={(e) => {
                            onBlur?.(e);
                            const trimmed = (inputValue || '').trim();
                            if (!trimmed) {
                                setFormatError(undefined);
                                onChange(null);
                                return;
                            }
                            if (isMonth) {
                                const parsed = parseMMYYYY(formatMonthYearInput(trimmed));
                                if (!parsed) {
                                    setFormatError(MONTH_YEAR_FORMAT_HINT);
                                    return;
                                }
                                setFormatError(undefined);
                                let corrected = parsed;
                                if (min && isBefore(corrected, min)) corrected = new Date(min.getFullYear(), min.getMonth(), 1);
                                else if (max && isAfter(corrected, max)) corrected = new Date(max.getFullYear(), max.getMonth(), 1);
                                const next = formatMMYYYY(corrected);
                                setInputValue(next);
                                onChange(next);
                                return;
                            }
                            const normalized = normalizeToDdMmYyyy(trimmed);
                            if (!normalized) {
                                setFormatError(DATE_FORMAT_HINT);
                                return;
                            }
                            setFormatError(undefined);
                            let correctedDate = parseYMD(normalized)!;
                            if (min && isBefore(correctedDate, min)) {
                                correctedDate = min;
                            } else if (max && isAfter(correctedDate, max)) {
                                correctedDate = max;
                            }
                            const next = toYMD(correctedDate);
                            setInputValue(next);
                            onChange(next);
                        }}
                        InputLabelProps={{
                            ...InputLabelProps,
                            sx: {
                                pointerEvents: 'auto',
                                cursor: hasTip ? 'help' : undefined,
                                ...(InputLabelProps as any)?.sx,
                            },
                            ref: (node: HTMLLabelElement | null) => {
                                tipLabelRef.current = node;
                                const originalRef = (InputLabelProps as any)?.ref;
                                if (typeof originalRef === 'function') originalRef(node);
                                else if (originalRef && typeof originalRef === 'object') originalRef.current = node;
                            },
                            onMouseEnter: (e: React.MouseEvent<HTMLLabelElement>) => {
                                (InputLabelProps as any)?.onMouseEnter?.(e);
                                enterTip();
                            },
                            onMouseLeave: (e: React.MouseEvent<HTMLLabelElement>) => {
                                (InputLabelProps as any)?.onMouseLeave?.(e);
                                leaveTip();
                            },
                        }}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={handleOpen}
                                        sx={{ p: 0.5 }}
                                        aria-label="open date picker"
                                    >
                                        <CalendarMonthIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        inputProps={{
                            ...params.inputProps,
                            ...inputProps,
                            style: {
                                textAlign: align,
                                ...inputProps?.style,
                            },
                        }}
                        sx={mergedSx}
                    />
                )}
            />
            {hasTip && (
                <Popover
                    open={tipOpen}
                    anchorEl={tipLabelRef.current}
                    onClose={leaveTip}
                    disableRestoreFocus
                    anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                    transformOrigin={{vertical: 'center', horizontal: 'left'}}
                    sx={{pointerEvents: 'none'}}
                    PaperProps={tipPaperProps}
                >
                    {popoverContent}
                </Popover>
            )}
            <Popover
                open={open}
                onClose={handleClose}
                anchorEl={anchorRef.current}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                // Ensure the popover stays within an open Dialog (if any) to avoid Modal aria-hidden conflicts
                disablePortal
                container={anchorRef.current ? (anchorRef.current.closest('.MuiDialog-root') as HTMLElement) || (anchorRef.current.parentElement as HTMLElement) : undefined}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        e.stopPropagation();
                        handleClose();
                    }
                }}
            >
                <Box sx={{ p: 1.5, width: 300 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box>
                            <IconButton size="small" onClick={handlePrevYear} disabled={!canGoPrevYear} sx={{ mr: 0.5 }}>
                                <KeyboardDoubleArrowLeftIcon />
                            </IconButton>
                            {!isMonth && (
                                <IconButton size="small" onClick={handlePrevMonth} disabled={!canGoPrev}>
                                    <ChevronLeftIcon />
                                </IconButton>
                            )}
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {isMonth ? viewYear : monthLabel}
                        </Typography>
                        <Box>
                            {!isMonth && (
                                <IconButton size="small" onClick={handleNextMonth} disabled={!canGoNext}>
                                    <ChevronRightIcon />
                                </IconButton>
                            )}
                            <IconButton size="small" onClick={handleNextYear} disabled={!canGoNextYear} sx={{ ml: 0.5 }}>
                                <KeyboardDoubleArrowRightIcon />
                            </IconButton>
                        </Box>
                    </Box>

                    {isMonth ? (
                        <Grid container columns={3} spacing={0.5}>
                            {MONTH_LABELS.map((label, monthIndex) => {
                                const d = new Date(viewYear, monthIndex, 1);
                                const disabledMonth = isDisabledDate(d);
                                const isSelected = !!selected
                                    && selected.getFullYear() === viewYear
                                    && selected.getMonth() === monthIndex;
                                return (
                                    <Grid key={label} size={{xs: 1}}>
                                        <Button
                                            variant={isSelected ? 'contained' : 'text'}
                                            color={isSelected ? 'primary' : 'inherit'}
                                            disabled={disabledMonth}
                                            onClick={() => handleSelect(d)}
                                            sx={{ minWidth: 0, width: '100%', height: 36 }}
                                        >
                                            {label}
                                        </Button>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    ) : (
                    <>
                    <Grid container columns={7} spacing={0.5} sx={{ mb: 0.5 }}>
                        {['Su','Mo','Tu','We','Th','Fr','Sa'].map((d) => (
                            <Grid key={d} size={{xs:1}}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>{d}</Typography>
                            </Grid>
                        ))}
                    </Grid>

                    <Grid container columns={7} spacing={0.5}>
                        {weeks.map((week, wi) => (
                            <React.Fragment key={wi}>
                                {week.map((d, di) => {
                                    if (!d) return (
                                        <Grid key={`e-${wi}-${di}`} size={{xs:1}}><Box sx={{ height: 36 }} /></Grid>
                                    );
                                    const disabledDay = isDisabledDate(d);
                                    const isSelected = selected ? isSameDay(d, selected) : false;
                                    return (
                                        <Grid key={`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${di}`} size={{xs:1}}>
                                            <Button
                                                variant={isSelected ? 'contained' : 'text'}
                                                color={isSelected ? 'primary' : 'inherit'}
                                                disabled={disabledDay}
                                                onClick={() => handleSelect(d)}
                                                sx={{ minWidth: 0, width: '100%', height: 36 }}
                                            >
                                                {d.getDate()}
                                            </Button>
                                        </Grid>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </Grid>
                    </>
                    )}
                </Box>
            </Popover>
        </>
    );
}
